import { isAnyOf, Middleware } from "@reduxjs/toolkit";
import { State } from "../state/types";
import { updateMetadata } from "../slices/metadata";
import { getSteps } from "components/Signup/steps";
import {
  removeAnimalById,
  switchAnimal,
  updateAnimalById,
  updateInsuranceById,
  updateInsurancesOfType,
  upsertDraft,
} from "../slices/animals";
import { updateCustomer } from "../slices/customer";
import {
  goToNextStep,
  goToPreviousStep,
  hydrateStep,
  updateActiveStep,
  updateNavigation,
} from "../slices/navigation";
import { wait } from "utils/utils";
import { updateRoute } from "./utils/updateRoute";
import { resetAction } from "..";
import { getStepParams } from "./utils/getStepParams";
import { parseUrl } from "components/Signup/flow/utils/parseUrlQuery";
import Router from "next/router";

const isUpdateAction = isAnyOf(
  updateAnimalById,
  updateCustomer,
  updateInsuranceById,
  updateMetadata,
  updateActiveStep,
  hydrateStep,
  removeAnimalById,
  updateInsurancesOfType
);

const steps = getSteps();

export const navigationMiddleware: Middleware<{}, State> =
  (storeApi) => (next) => async (action) => {
    if (isAnyOf(switchAnimal)(action)) {
      const { stepName } = parseUrl(Router.query);

      await Router.push({
        pathname: `${stepName}/${action.payload}`,
        ...(Router.query.id ? { query: { id: Router.query.id } } : {}),
      });
    }

    if (isAnyOf(hydrateStep)(action)) {
      const state = storeApi.getState();

      const initialStep = steps.find((step) => step.name === action.payload);

      // Reset to start if we can't find the step
      if (!initialStep) {
        updateRoute({ route: steps[0].name, state, replace: true });

        return next(action);
      }

      if (state.metadata.purchaseCompleted && action.payload !== "thank-you") {
        storeApi.dispatch(resetAction());

        return next(action);
      }

      // Trying to access a later step than they were on
      if (
        (steps.find((step) => step.name === state.navigation.activeStep)
          ?.index ?? 99) <
        (steps.find((step) => step.name === action.payload)?.index ?? 0)
      ) {
        updateRoute({
          route: state.navigation.activeStep,
          state,
          replace: true,
        });

        return next(action);
      }
    }

    const result = next(action);

    if (isAnyOf(updateActiveStep)(action)) {
      const state = storeApi.getState();

      // Always do an upsert when go to the loading page
      if (action.payload === "loading") {
        storeApi.dispatch(upsertDraft(undefined));
      }

      if (state.metadata.purchaseCompleted && action.payload !== "thank-you") {
        storeApi.dispatch(resetAction());

        return result;
      }
    }

    // GO TO NEXT STEP
    if (isAnyOf(goToNextStep)(action)) {
      const state = storeApi.getState();

      const { next, isValid } =
        steps.find((step) => step.name === state.navigation.activeStep) ||
        steps[0];

      const nextStepName = next(
        getStepParams(storeApi),
        action.payload.overrideStep
      );

      await wait(0.5);

      storeApi.dispatch(
        updateNavigation({ isValid: isValid(getStepParams(storeApi)) })
      );

      updateRoute({
        route: nextStepName,
        state,
        replace: action.payload.replace,
      });
    }

    // GO TO PREVIOUS STEP
    if (isAnyOf(goToPreviousStep)(action)) {
      const state = storeApi.getState();

      const { previous } =
        steps.find((step) => step.name === state.navigation.activeStep) ||
        steps[0];

      const nextStepName = previous && previous(getStepParams(storeApi));

      await wait(0.5);

      if (nextStepName) {
        updateRoute({
          route: nextStepName,
          state,
          replace: action.payload.replace,
        });
      } else {
        Router.back();
      }
    }

    // Run the validation on every update
    if (isUpdateAction(action)) {
      const nextStep = steps.find(
        (s) => s.name === storeApi.getState().navigation.activeStep
      );

      const isValid = nextStep?.isValid(getStepParams(storeApi));

      storeApi.dispatch(updateNavigation({ isValid: Boolean(isValid) }));
    }

    return result;
  };
